import * as React from 'react';
import {type InputProps} from './Input';

const isEmptyValue = (value: InputProps['value'] | InputProps['defaultValue']) => {
  if (value === undefined || value === null) return true;
  return String(value).length === 0;
};

const getInputEmptyState = ({
  empty,
  defaultValue,
  value,
}: {
  empty?: boolean;
  value?: InputProps['value'];
  defaultValue?: InputProps['defaultValue'];
}) => {
  if (typeof empty !== 'undefined') return empty;
  if (typeof value !== 'undefined') return isEmptyValue(value);
  return isEmptyValue(defaultValue);
};

export function useInputValueState({
  empty,
  value,
  defaultValue,
}: {
  empty?: boolean;
  value?: InputProps['value'];
  defaultValue?: InputProps['defaultValue'];
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isControlled = typeof empty !== 'undefined' || typeof value !== 'undefined';
  const propsAreEmpty = getInputEmptyState({empty, value, defaultValue});
  const [nativeIsEmpty, setNativeIsEmpty] = React.useState(propsAreEmpty);
  const isEmpty = isControlled ? propsAreEmpty : nativeIsEmpty;

  const setNativeValue = React.useCallback(
    (nextValue: string) => {
      if (isControlled) return;

      const nextIsEmpty = nextValue.length === 0;
      setNativeIsEmpty(prevIsEmpty =>
        prevIsEmpty === nextIsEmpty ? prevIsEmpty : nextIsEmpty,
      );
    },
    [isControlled],
  );

  const syncValueState = React.useCallback(() => {
    if (!inputRef.current) return;
    setNativeValue(inputRef.current.value);
  }, [setNativeValue]);

  // Form libraries can preload uncontrolled values through refs without props.
  React.useLayoutEffect(() => {
    syncValueState();
  });

  React.useEffect(() => {
    const input = inputRef.current;
    const form = input?.form;

    if (isControlled || !form) return;

    const handleReset = () => {
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(syncValueState);
        return;
      }

      globalThis.setTimeout(syncValueState, 0);
    };

    form.addEventListener('reset', handleReset);
    return () => form.removeEventListener('reset', handleReset);
  }, [isControlled, syncValueState]);

  return {
    inputRef,
    isControlled,
    isEmpty,
    setNativeValue,
    syncValueState,
  };
}
